import React, { PropsWithChildren } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Auth/permission failures are not transient — retrying them just multiplies
// the request count (the default of 3 retries turned every pre-auth 401 into
// four round trips) and delays the error the UI needs to react to.
const isNotRetryable = (error: any) => {
  const status = error?.response?.status ?? error?.request?.status;
  return status === 400 || status === 401 || status === 403 || status === 404;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => !isNotRetryable(error) && failureCount < 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    // `post`/`put`/`patch`/`remove` swallow HTTP errors into a resolved
    // `{ statusCode }` value (see core/utils/responseHandler), so mutations
    // never reject and a retry policy here would be dead config either way.
    mutations: { retry: false },
  },
});

const ApiProvider = ({ children }: PropsWithChildren<unknown>) => {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

export default ApiProvider;
